import React, { useEffect, useState } from 'react'
import { Alert, Button, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import VerifiedIcon from '@mui/icons-material/Verified'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import dataGridSx from '../kit/dataGridSx'
import { SoftChip } from '../kit/Chips'
import { formatDate } from '../kit/helpers'
import AccreditationDialog from './AccreditationDialog'
import SignOffDialog from './SignOffDialog'
import { ACCREDITATION_STATUS, VERSION_STATUS_COLOR, accreditationMeta } from './trainingMeta'

// Published e-learning course versions and where each stands with CPD UK.
const CourseVersions = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)
  const [signingOff, setSigningOff] = useState(null)

  const load = () => {
    API.get('/admincrm/training/courses')
      .then((res) => {
        if (res.status === 200) {
          setRows(res.data)
          setError(null)
        } else {
          setError(res.data?.error ?? 'Unable to load the course versions')
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const columns = [
    { field: 'code', headerName: 'Code', width: 90 },
    { field: 'title', headerName: 'Title', minWidth: 220, flex: 1 },
    { field: 'version', headerName: 'Version', width: 100 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) =>
        value ? <SoftChip label={value} color={VERSION_STATUS_COLOR[value] ?? '#64748B'} /> : '',
    },
    {
      field: 'publishedAt',
      headerName: 'Published',
      width: 120,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: ({ value }) => formatDate(value) || 'Not published',
    },
    {
      field: 'signOff',
      headerName: 'Sign-off',
      minWidth: 170,
      flex: 0.6,
      sortable: false,
      valueGetter: (value) => [value?.by, value?.on].filter(Boolean).join(', '),
      renderCell: ({ row }) =>
        row.signOff?.by ? (
          <Tooltip title={row.signOff.note ?? ''}>
            <Stack>
              <Typography variant="body2">{row.signOff.by}</Typography>
              <Typography variant="caption" color="text.secondary">
                {/^\d{4}-\d{2}-\d{2}/.test(row.signOff.on ?? '') ? formatDate(row.signOff.on) : row.signOff.on}
              </Typography>
            </Stack>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not signed off
          </Typography>
        ),
    },
    {
      field: 'pathways',
      headerName: 'Pathways',
      minWidth: 200,
      flex: 0.8,
      sortable: false,
      valueGetter: (value, row) => (row.course?.pathways ?? []).map((pathway) => pathway.title).join(', '),
      renderCell: ({ value }) => (
        <Tooltip title={value}>
          <Typography variant="body2" noWrap>
            {value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'accreditation',
      headerName: 'Accreditation',
      width: 150,
      valueGetter: (value) => accreditationMeta(value?.status).label,
      renderCell: ({ row }) => {
        const meta = accreditationMeta(row.accreditation?.status)
        return (
          <Tooltip title={row.accreditation?.cpdReference ? `CPD UK reference ${row.accreditation.cpdReference}` : ''}>
            <span>
              <SoftChip
                label={meta.label}
                color={meta.color}
                icon={row.accreditation?.status === 'accredited' ? <VerifiedIcon /> : undefined}
              />
            </span>
          </Tooltip>
        )
      },
    },
    { field: 'feedbackCount', headerName: 'Feedback', width: 100, type: 'number' },
    {
      field: 'actions',
      headerName: '',
      width: 240,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1} alignItems="center" height="100%">
          {row.status === 'in-review' && (
            <Button size="small" variant="contained" onClick={() => setSigningOff(row)}>
              Sign off
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={() => setEditing(row)}>
            Accreditation
          </Button>
        </Stack>
      ),
    },
  ]

  return (
    <Stack gap={2}>
      <PageHeader
        eyebrow="Training"
        title="E-learning courses"
        subtitle="Every version of each course, with its Clinical Safety Officer sign-off and CPD UK accreditation status. Content is loaded from the repository; the sign-off and the accreditation record are made here. A course in review is only visible to organisations where preview is switched on."
      />
      {error && <Alert severity="error">{error}</Alert>}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        autoHeight
        // Few rows and columns: render them all, so none is unreachable off screen.
        disableVirtualization
        getRowHeight={() => 56}
        sx={{ ...dataGridSx, '& .MuiDataGrid-row:hover': { backgroundColor: '#F8FAFC' } }}
        localeText={{ noRowsLabel: 'No course versions have been published yet.' }}
      />
      <SignOffDialog
        version={signingOff}
        onClose={() => setSigningOff(null)}
        onSaved={() => {
          setSigningOff(null)
          load()
        }}
      />
      <AccreditationDialog
        version={editing}
        statuses={ACCREDITATION_STATUS}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          load()
        }}
      />
    </Stack>
  )
}

export default CourseVersions
